import { adminApi, ensureArray } from '../services/api';
import { useState, useEffect } from 'react';
import { TherapistSpecializationPills } from '../components/TherapistSpecializationPills';
import { resolveTherapistDisplayName } from '@lighthouse/shared';

interface Therapist {
  id: string;
  displayName?: string | null;
  cassonNumber: string;
  licenseBody: string;
  specializations: string[];
  languages: string[];
  status: 'verified' | 'pending' | 'suspended';
  gender: string;
  hourlyRateNgn: number;
  ratingAvg: number;
  ratingCount: number;
  isFeatured?: boolean;
  featuredSortOrder?: number;
  createdAt: string;
  user: { email: string; phone: string; createdAt: string };
}

const STATUS_FILTERS = ['all', 'verified', 'pending', 'suspended'] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

const STATUS_BADGE: Record<string, string> = {
  verified: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  suspended: 'bg-red-100 text-red-700',
};

export function TherapistsPage() {
  const [therapists, setTherapists] = useState<Therapist[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [actioning, setActioning] = useState<string | null>(null);

  const load = (f: StatusFilter) => {
    setLoading(true);
    const q = f === 'all' ? '' : `?status=${f}`;
    adminApi<Therapist[]>(`/admin/therapists${q}`)
      .then((data) => setTherapists(ensureArray(data)))
      .catch(() => setTherapists([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(filter);
  }, [filter]);

  const updateStatus = async (id: string, status: 'verified' | 'suspended') => {
    setActioning(id);
    try {
      await adminApi(`/admin/therapists/${id}/verify`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      });
      load(filter);
    } finally {
      setActioning(null);
    }
  };

  const toggleFeatured = async (id: string, isFeatured: boolean, featuredSortOrder?: number) => {
    setActioning(id);
    try {
      await adminApi(`/admin/therapists/${id}/featured`, {
        method: 'PUT',
        body: JSON.stringify({ isFeatured, featuredSortOrder }),
      });
      load(filter);
    } finally {
      setActioning(null);
    }
  };

  const moveFeatured = async (id: string, direction: 'up' | 'down') => {
    const featured = therapists
      .filter((t) => t.isFeatured)
      .sort((a, b) => (a.featuredSortOrder ?? 0) - (b.featuredSortOrder ?? 0));
    const index = featured.findIndex((t) => t.id === id);
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    const current = featured[index];
    const swap = featured[swapIndex];
    if (!current || !swap) return;
    setActioning(id);
    try {
      await Promise.all([
        adminApi(`/admin/therapists/${current.id}/featured`, {
          method: 'PUT',
          body: JSON.stringify({
            isFeatured: true,
            featuredSortOrder: swap.featuredSortOrder ?? swapIndex,
          }),
        }),
        adminApi(`/admin/therapists/${swap.id}/featured`, {
          method: 'PUT',
          body: JSON.stringify({
            isFeatured: true,
            featuredSortOrder: current.featuredSortOrder ?? index,
          }),
        }),
      ]);
      load(filter);
    } finally {
      setActioning(null);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900">All Therapists</h1>

      {/* Status filter tabs */}
      <div className="mt-4 flex gap-2">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`rounded-full border px-4 py-1.5 text-sm font-medium capitalize transition-colors ${
              filter === f
                ? 'border-indigo-600 bg-indigo-600 text-white'
                : 'border-gray-200 bg-white text-gray-600 hover:border-indigo-300'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-8 text-center text-gray-400">Loading…</div>
      ) : therapists.length === 0 ? (
        <div className="card mt-8 text-center text-gray-500">No therapists found.</div>
      ) : (
        <div className="mt-6 overflow-hidden rounded-xl border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Therapist</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Specializations</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Rate</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Rating</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Status</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Featured</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {therapists.map((t) => (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">
                      {resolveTherapistDisplayName(t.displayName, t.user.email)}
                    </p>
                    <p className="text-xs text-gray-500">{t.user.email}</p>
                    <p className="text-xs text-gray-400">{t.user.phone}</p>
                    <p className="mt-0.5 font-mono text-xs text-gray-400">{t.cassonNumber}</p>
                  </td>
                  <td className="px-4 py-3">
                    <TherapistSpecializationPills specializations={t.specializations} limit={3} />
                  </td>
                  <td className="px-4 py-3 text-gray-700">₦{t.hourlyRateNgn.toLocaleString()}</td>
                  <td className="px-4 py-3 text-gray-700">
                    ★ {t.ratingAvg.toFixed(1)}{' '}
                    <span className="text-xs text-gray-400">({t.ratingCount})</span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${STATUS_BADGE[t.status]}`}
                    >
                      {t.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {t.status === 'verified' ? (
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() =>
                            toggleFeatured(t.id, !t.isFeatured, t.featuredSortOrder ?? 0)
                          }
                          disabled={actioning === t.id}
                          className={`rounded px-2 py-1 text-xs font-semibold ${
                            t.isFeatured
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {t.isFeatured ? `★ #${t.featuredSortOrder ?? 0}` : 'Feature'}
                        </button>
                        {t.isFeatured && (
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => moveFeatured(t.id, 'up')}
                              className="rounded bg-gray-100 px-1.5 text-xs"
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              onClick={() => moveFeatured(t.id, 'down')}
                              className="rounded bg-gray-100 px-1.5 text-xs"
                            >
                              ↓
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      {t.status !== 'verified' && (
                        <button
                          onClick={() => updateStatus(t.id, 'verified')}
                          disabled={actioning === t.id}
                          className="rounded bg-green-600 px-2 py-1 text-xs text-white hover:bg-green-700 disabled:opacity-50"
                        >
                          Approve
                        </button>
                      )}
                      {t.status !== 'suspended' && (
                        <button
                          onClick={() => updateStatus(t.id, 'suspended')}
                          disabled={actioning === t.id}
                          className="rounded bg-red-600 px-2 py-1 text-xs text-white hover:bg-red-700 disabled:opacity-50"
                        >
                          Suspend
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
